import { useCallback, useEffect, useState } from "react";
import { useSessionContext } from "@/integrations/supabase/SessionContextProvider";
import {
  fetchSupabaseCollection,
  loadCachedValue,
  replaceSupabaseCollection,
  saveCachedValue,
  subscribeToCachedValue,
} from "@/integrations/supabase/contentStore";

const STORAGE_KEYS = {
  products: "afiliabr_admin_products",
  deals: "afiliabr_admin_deals",
};

const LEGACY_PRODUCT_SLUGS = new Set([
  "samsung-smart-tv-50-crystal-uhd-4k-2024",
  "iphone-15-128gb-preto",
  "notebook-dell-inspiron-15-i5-16gb-512gb",
  "playstation-5-slim-controle-extra",
  "air-fryer-philips-walita-4-1l-digital",
  "fone-jbl-tune-770nc-bluetooth-anc",
]);

const LEGACY_DEAL_SLUGS = new Set([
  "samsung-smart-tv-50-crystal-uhd-4k-2024",
  "iphone-15-128gb-preto",
  "playstation-5-slim-controle-extra",
  "notebook-dell-inspiron-15-i5-16gb-512gb",
  "air-fryer-philips-walita-4-1l-digital",
  "fone-jbl-tune-770nc-bluetooth-anc",
  "samsung-galaxy-watch-6-44mm-preto",
  "geladeira-brastemp-frost-free-375l-inox",
  "lg-oled-50-evo-c3-4k-120hz",
  "tcl-50-p635-4k-google-tv",
  "sony-bravia-x80l-50-4k-google-tv",
  "kindle-paperwhite-16gb-preto",
]);

export type AdminProduct = {
  id: number;
  name: string;
  slug: string;
  category: string;
  categorySlug: string;
  subcategory: string;
  subcategorySlug: string;
  image: string;
  galleryImages: string[];
  overviewText: string;
  price: number;
  originalPrice: number;
  discount: number;
  rating: number;
  reviews: number;
  marketplace: string;
  affiliateLink: string;
  badge: string | null;
  freeShipping: boolean;
  fulfillment: boolean;
  officialStore: boolean;
  officialStoreName: string;
  showInDailyDeals: boolean;
  showInFeatured: boolean;
};

type StoredAdminProduct = Partial<AdminProduct> & {
  full?: boolean;
};

export type AdminDeal = {
  id: number;
  name: string;
  slug: string;
  category: string;
  categorySlug: string;
  image: string;
  price: number;
  originalPrice: number;
  discount: number;
  rating: number;
  reviews: number;
  marketplace: string;
  affiliateLink: string;
  freeShipping: boolean;
  badge: string | null;
  endsAt: number;
  sold: number;
  stock: number;
  isFlash: boolean;
};

type ProductRow = {
  id: number | string;
  name: string;
  slug: string;
  category: string;
  category_slug: string;
  subcategory: string;
  subcategory_slug: string;
  image: string;
  gallery_images: string[] | null;
  overview_text?: string | null;
  price: number | string;
  original_price: number | string;
  discount: number | string;
  rating: number | string;
  reviews: number | string;
  marketplace: string;
  affiliate_link: string;
  badge: string | null;
  free_shipping: boolean;
  fulfillment: boolean;
  official_store: boolean;
  official_store_name?: string | null;
  show_in_daily_deals: boolean;
  show_in_featured: boolean;
};

type DealRow = {
  id: number | string;
  name: string;
  slug: string;
  category: string;
  category_slug: string;
  image: string;
  price: number | string;
  original_price: number | string;
  discount: number | string;
  rating: number | string;
  reviews: number | string;
  marketplace: string;
  affiliate_link: string;
  free_shipping: boolean;
  badge: string | null;
  ends_at: number | string;
  sold: number | string;
  stock: number | string;
  is_flash: boolean;
};

function normalizeGalleryImages(images: string[] | undefined, primaryImage: string) {
  return (Array.isArray(images) ? images : [])
    .map((image) => image.trim())
    .filter((image) => image && image !== primaryImage)
    .slice(0, 2);
}

function normalizeAdminProduct(product: StoredAdminProduct): AdminProduct {
  const officialStoreName = String(product.officialStoreName ?? "").trim();

  return {
    id: Number(product.id ?? 0),
    name: product.name ?? "",
    slug: product.slug ?? "",
    category: product.category ?? "",
    categorySlug: product.categorySlug ?? "",
    subcategory: product.subcategory ?? product.category ?? "",
    subcategorySlug: product.subcategorySlug ?? product.categorySlug ?? "",
    image: product.image ?? "",
    galleryImages: normalizeGalleryImages(product.galleryImages, product.image ?? ""),
    overviewText: String(product.overviewText ?? ""),
    price: Number(product.price ?? 0),
    originalPrice: Number(product.originalPrice ?? 0),
    discount: Number(product.discount ?? 0),
    rating: Number(product.rating ?? 0),
    reviews: Number(product.reviews ?? 0),
    marketplace: product.marketplace ?? "",
    affiliateLink: product.affiliateLink ?? "",
    badge: product.badge ?? null,
    freeShipping: Boolean(product.freeShipping),
    fulfillment: Boolean(product.fulfillment ?? product.full),
    officialStore: Boolean(product.officialStore || officialStoreName),
    officialStoreName,
    showInDailyDeals: Boolean(product.showInDailyDeals),
    showInFeatured: Boolean(product.showInFeatured),
  };
}

function normalizeAdminProducts(products: StoredAdminProduct[]) {
  return products.map((product) => normalizeAdminProduct(product));
}

function normalizeAdminDeal(deal: Partial<AdminDeal>): AdminDeal {
  return {
    id: Number(deal.id ?? 0),
    name: deal.name ?? "",
    slug: deal.slug ?? "",
    category: deal.category ?? "",
    categorySlug: deal.categorySlug ?? "",
    image: deal.image ?? "",
    price: Number(deal.price ?? 0),
    originalPrice: Number(deal.originalPrice ?? 0),
    discount: Number(deal.discount ?? 0),
    rating: Number(deal.rating ?? 0),
    reviews: Number(deal.reviews ?? 0),
    marketplace: deal.marketplace ?? "",
    affiliateLink: deal.affiliateLink ?? "",
    freeShipping: Boolean(deal.freeShipping),
    badge: deal.badge ?? null,
    endsAt: Number(deal.endsAt ?? Date.now()),
    sold: Number(deal.sold ?? 0),
    stock: Number(deal.stock ?? 0),
    isFlash: Boolean(deal.isFlash),
  };
}

function normalizeAdminDeals(deals: Partial<AdminDeal>[]) {
  return deals.map((deal) => normalizeAdminDeal(deal));
}

function isLegacySeededProducts(products: StoredAdminProduct[]) {
  return (
    Array.isArray(products) &&
    products.length > 0 &&
    products.every((product) => {
      const slug = product?.slug ?? "";
      const affiliateLink = product?.affiliateLink ?? "";
      const image = product?.image ?? "";

      return LEGACY_PRODUCT_SLUGS.has(slug) && (affiliateLink === "#" || image.includes("readdy.ai"));
    })
  );
}

function isLegacySeededDeals(deals: Partial<AdminDeal>[]) {
  return (
    Array.isArray(deals) &&
    deals.length > 0 &&
    deals.every((deal) => {
      const slug = deal?.slug ?? "";
      const affiliateLink = deal?.affiliateLink ?? "";
      const image = deal?.image ?? "";

      return LEGACY_DEAL_SLUGS.has(slug) && (affiliateLink === "#" || image.includes("readdy.ai"));
    })
  );
}

const defaultProducts: AdminProduct[] = [];
const defaultDeals: AdminDeal[] = [];

function loadProductsFromCache() {
  const stored = loadCachedValue<StoredAdminProduct[]>(STORAGE_KEYS.products, defaultProducts);
  const source = isLegacySeededProducts(stored) ? defaultProducts : stored;
  return normalizeAdminProducts(source);
}

function loadDealsFromCache() {
  const stored = loadCachedValue<Partial<AdminDeal>[]>(STORAGE_KEYS.deals, defaultDeals);
  const source = isLegacySeededDeals(stored) ? defaultDeals : stored;
  return normalizeAdminDeals(source);
}

function saveProductsToCache(products: AdminProduct[]) {
  saveCachedValue(STORAGE_KEYS.products, products);
}

function saveDealsToCache(deals: AdminDeal[]) {
  saveCachedValue(STORAGE_KEYS.deals, deals);
}

function mapProductRow(row: ProductRow): AdminProduct {
  return normalizeAdminProduct({
    id: Number(row.id),
    name: row.name,
    slug: row.slug,
    category: row.category,
    categorySlug: row.category_slug,
    subcategory: row.subcategory,
    subcategorySlug: row.subcategory_slug,
    image: row.image,
    galleryImages: Array.isArray(row.gallery_images) ? row.gallery_images : [],
    overviewText: row.overview_text ?? "",
    price: Number(row.price ?? 0),
    originalPrice: Number(row.original_price ?? 0),
    discount: Number(row.discount ?? 0),
    rating: Number(row.rating ?? 0),
    reviews: Number(row.reviews ?? 0),
    marketplace: row.marketplace,
    affiliateLink: row.affiliate_link,
    badge: row.badge,
    freeShipping: row.free_shipping,
    fulfillment: row.fulfillment,
    officialStore: row.official_store,
    officialStoreName: row.official_store_name ?? "",
    showInDailyDeals: row.show_in_daily_deals,
    showInFeatured: row.show_in_featured,
  });
}

function mapDealRow(row: DealRow): AdminDeal {
  return normalizeAdminDeal({
    id: Number(row.id),
    name: row.name,
    slug: row.slug,
    category: row.category,
    categorySlug: row.category_slug,
    image: row.image,
    price: Number(row.price ?? 0),
    originalPrice: Number(row.original_price ?? 0),
    discount: Number(row.discount ?? 0),
    rating: Number(row.rating ?? 0),
    reviews: Number(row.reviews ?? 0),
    marketplace: row.marketplace,
    affiliateLink: row.affiliate_link,
    freeShipping: row.free_shipping,
    badge: row.badge,
    endsAt: Number(row.ends_at ?? Date.now()),
    sold: Number(row.sold ?? 0),
    stock: Number(row.stock ?? 0),
    isFlash: row.is_flash,
  });
}

function mapProductToRow(product: AdminProduct) {
  return {
    id: product.id,
    name: product.name,
    slug: product.slug,
    category: product.category,
    category_slug: product.categorySlug,
    subcategory: product.subcategory,
    subcategory_slug: product.subcategorySlug,
    image: product.image,
    gallery_images: product.galleryImages,
    overview_text: product.overviewText,
    price: product.price,
    original_price: product.originalPrice,
    discount: product.discount,
    rating: product.rating,
    reviews: product.reviews,
    marketplace: product.marketplace,
    affiliate_link: product.affiliateLink,
    badge: product.badge,
    free_shipping: product.freeShipping,
    fulfillment: product.fulfillment,
    official_store: product.officialStore,
    official_store_name: product.officialStoreName,
    show_in_daily_deals: product.showInDailyDeals,
    show_in_featured: product.showInFeatured,
  };
}

function mapDealToRow(deal: AdminDeal) {
  return {
    id: deal.id,
    name: deal.name,
    slug: deal.slug,
    category: deal.category,
    category_slug: deal.categorySlug,
    image: deal.image,
    price: deal.price,
    original_price: deal.originalPrice,
    discount: deal.discount,
    rating: deal.rating,
    reviews: deal.reviews,
    marketplace: deal.marketplace,
    affiliate_link: deal.affiliateLink,
    free_shipping: deal.freeShipping,
    badge: deal.badge,
    ends_at: deal.endsAt,
    sold: deal.sold,
    stock: deal.stock,
    is_flash: deal.isFlash,
  };
}

async function fetchProductsFromSupabase() {
  const rows = await fetchSupabaseCollection<ProductRow>("products", "id");
  return rows.map((row) => mapProductRow(row));
}

async function fetchDealsFromSupabase() {
  const rows = await fetchSupabaseCollection<DealRow>("deals", "id");
  return rows.map((row) => mapDealRow(row));
}

async function syncProductsToSupabase(products: AdminProduct[]) {
  const rows = await replaceSupabaseCollection({
    table: "products",
    rows: products.map((product) => mapProductToRow(product)),
    orderBy: "id",
  });

  return (rows as ProductRow[]).map((row) => mapProductRow(row));
}

async function syncDealsToSupabase(deals: AdminDeal[]) {
  const rows = await replaceSupabaseCollection({
    table: "deals",
    rows: deals.map((deal) => mapDealToRow(deal)),
    orderBy: "id",
  });

  return (rows as DealRow[]).map((row) => mapDealRow(row));
}

export function getStoredAdminProducts() {
  return loadProductsFromCache();
}

export function getStoredAdminDeals() {
  return loadDealsFromCache();
}

export function useAdminProducts() {
  const { session } = useSessionContext();
  const [products, setProducts] = useState<AdminProduct[]>(loadProductsFromCache);

  useEffect(() => {
    return subscribeToCachedValue(STORAGE_KEYS.products, defaultProducts, (value) => {
      setProducts(normalizeAdminProducts(value as StoredAdminProduct[]));
    });
  }, []);

  useEffect(() => {
    let active = true;

    const sync = async () => {
      try {
        const remote = await fetchProductsFromSupabase();
        if (!active) return;

        if (remote.length > 0) {
          setProducts(remote);
          saveProductsToCache(remote);
          return;
        }

        const cached = loadProductsFromCache();
        if (!session || cached.length === 0) return;

        const seeded = await syncProductsToSupabase(cached);
        if (!active) return;

        setProducts(seeded);
        saveProductsToCache(seeded);
      } catch {
        // ignore and keep cache fallback
      }
    };

    void sync();

    return () => {
      active = false;
    };
  }, [session]);

  const save = useCallback((nextData: AdminProduct[]) => {
    const normalized = normalizeAdminProducts(nextData);
    setProducts(normalized);
    saveProductsToCache(normalized);

    void syncProductsToSupabase(normalized).then((remote) => {
      setProducts(remote);
      saveProductsToCache(remote);
    }).catch(() => {
      // ignore and keep cache fallback
    });
  }, []);

  const addProduct = useCallback((product: Omit<AdminProduct, "id">) => {
    const newId = Math.max(0, ...products.map((item) => item.id)) + 1;
    save([...products, normalizeAdminProduct({ ...product, id: newId })]);
  }, [products, save]);

  const updateProduct = useCallback((id: number, data: Partial<AdminProduct>) => {
    save(products.map((item) => (item.id === id ? normalizeAdminProduct({ ...item, ...data, id }) : item)));
  }, [products, save]);

  const deleteProduct = useCallback((id: number) => {
    save(products.filter((item) => item.id !== id));
  }, [products, save]);

  const resetToDefault = useCallback(() => {
    save(defaultProducts);
  }, [save]);

  return { products, addProduct, updateProduct, deleteProduct, resetToDefault };
}

export function useAdminDeals() {
  const { session } = useSessionContext();
  const [deals, setDeals] = useState<AdminDeal[]>(loadDealsFromCache);

  useEffect(() => {
    return subscribeToCachedValue(STORAGE_KEYS.deals, defaultDeals, (value) => {
      setDeals(normalizeAdminDeals(value as Partial<AdminDeal>[]));
    });
  }, []);

  useEffect(() => {
    let active = true;

    const sync = async () => {
      try {
        const remote = await fetchDealsFromSupabase();
        if (!active) return;

        if (remote.length > 0) {
          setDeals(remote);
          saveDealsToCache(remote);
          return;
        }

        const cached = loadDealsFromCache();
        if (!session || cached.length === 0) return;

        const seeded = await syncDealsToSupabase(cached);
        if (!active) return;

        setDeals(seeded);
        saveDealsToCache(seeded);
      } catch {
        // ignore and keep cache fallback
      }
    };

    void sync();

    return () => {
      active = false;
    };
  }, [session]);

  const save = useCallback((nextData: AdminDeal[]) => {
    const normalized = normalizeAdminDeals(nextData);
    setDeals(normalized);
    saveDealsToCache(normalized);

    void syncDealsToSupabase(normalized).then((remote) => {
      setDeals(remote);
      saveDealsToCache(remote);
    }).catch(() => {
      // ignore and keep cache fallback
    });
  }, []);

  const addDeal = useCallback((deal: Omit<AdminDeal, "id">) => {
    const newId = Math.max(0, ...deals.map((item) => item.id)) + 1;
    save([...deals, normalizeAdminDeal({ ...deal, id: newId })]);
  }, [deals, save]);

  const updateDeal = useCallback((id: number, data: Partial<AdminDeal>) => {
    save(deals.map((item) => (item.id === id ? normalizeAdminDeal({ ...item, ...data, id }) : item)));
  }, [deals, save]);

  const deleteDeal = useCallback((id: number) => {
    save(deals.filter((item) => item.id !== id));
  }, [deals, save]);

  const resetToDefault = useCallback(() => {
    save(defaultDeals);
  }, [save]);

  return { deals, addDeal, updateDeal, deleteDeal, resetToDefault };
}